import { Check, X, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadFile, fmtDateTime, formatApiError } from "@/lib/api";
import { STATUS, TIMELINE_STEPS } from "@/data/portal";

export const StatusTimeline = ({ status }) => {
  const rejected = status === "rejected";
  const idx = TIMELINE_STEPS.indexOf(status);
  return (
    <ol className="grid grid-cols-4 gap-2" data-testid="status-timeline">
      {TIMELINE_STEPS.map((s, i) => {
        const done = !rejected && i <= idx;
        return (
          <li key={s} className="flex flex-col gap-2">
            <div className={`h-1.5 rounded-full ${done ? "bg-[#C20500]" : "bg-slate-200"}`} />
            <span className={`flex items-center gap-1 text-xs font-medium ${done ? "text-slate-900" : "text-slate-400"}`}>
              {done && <Check className="h-3 w-3 text-[#C20500]" />} {STATUS[s].label}
            </span>
          </li>
        );
      })}
      {rejected && (
        <li className="col-span-4 flex items-center gap-2 rounded-md bg-[#FBEDEA] px-3 py-2 text-xs font-semibold text-red-700"><X className="h-3.5 w-3.5" /> This request was closed as rejected. See notes below.</li>
      )}
    </ol>
  );
};

export const HistoryList = ({ history = [] }) => (
  <ol className="relative space-y-5 border-l border-slate-200 pl-5" data-testid="request-history">
    {[...history].reverse().map((h, i) => (
      <li key={i} className="relative">
        <span className="absolute -left-[25px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#C20500] ring-1 ring-slate-200" />
        <p className="text-sm font-semibold text-slate-900">{STATUS[h.status]?.label || h.status}</p>
        {h.note && <p className="mt-0.5 text-sm text-slate-600">{h.note}</p>}
        <p className="mono mt-0.5 text-[11px] text-slate-400">{fmtDateTime(h.at)}</p>
      </li>
    ))}
  </ol>
);

export const AttachmentList = ({ files = [] }) => {
  if (!files.length) return <p className="text-sm text-slate-400">No attachments</p>;
  const get = async (f) => {
    try {
      await downloadFile(f.id, f.filename);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <ul className="space-y-2" data-testid="attachment-list">
      {files.map((f) => (
        <li key={f.id}>
          <button type="button" onClick={() => get(f)} className="flex w-full items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:border-slate-400" data-testid={`attachment-download-${f.id}`}>
            <span className="flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-slate-500" /><span className="truncate">{f.filename}</span></span>
            <Download className="h-4 w-4 shrink-0 text-slate-500" />
          </button>
        </li>
      ))}
    </ul>
  );
};

export const DetailGrid = ({ rows }) => (
  <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
    {rows.filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
      <div key={k}>
        <dt className="mono text-[11px] uppercase tracking-wider text-slate-400">{k}</dt>
        <dd className="mt-0.5 break-words text-sm text-slate-900">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
      </div>
    ))}
  </dl>
);
